/**
 * DialogForm - Custom Dialog wrapper component
 * This is a custom component, NOT from shadcn.
 * Provides a form dialog with loading states, scroll area, and escape blocking.
 */

import React, { useEffect, useState } from 'react';
import { cn } from '@ais-ui/utils';
import { LoadingSpinner } from './LoadingSpinner';
import { Button } from './Button';
import { ScrollArea } from './ScrollArea';
import { useToast } from './Toast/useToast';
import { ToasterInDialog } from './Toast/Toaster';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';

interface DialogFormProps extends React.HTMLAttributes<HTMLDivElement> {
  showDialog: boolean;
  onOpenChange: (show: boolean) => void;
  title: string;
  description?: string;
  isLoading?: boolean;
  hasLoaded?: boolean;
  children?: React.ReactNode;
  buttonId: string;
  buttonText: string;
  buttonDisabled: boolean;
  buttonOnClick: () => void;
  className?: string;
  observeResize?: boolean;
  onClose?: () => void;
}

/**
 * Custom hook to block dialog escape behavior
 * Used by child components to prevent dialog closure when needed
 */
export const useBlockDialogEscape = (shouldBlock: boolean) => {
  useEffect(() => {
    const event = new CustomEvent('dialog-escape-block', {
      detail: { blocked: shouldBlock },
    });
    window.dispatchEvent(event);

    return () => {
      if (shouldBlock) {
        const unblockEvent = new CustomEvent('dialog-escape-block', {
          detail: { blocked: false },
        });
        window.dispatchEvent(unblockEvent);
      }
    };
  }, [shouldBlock]);
};

/**
 * Custom hook to warn about unsaved changes when exiting dialog
 */
export const useDialogExitWarning = (hasUnsavedChanges: boolean) => {
  useBlockDialogEscape(hasUnsavedChanges);
};

export const DialogForm = ({
  showDialog,
  onOpenChange,
  title,
  description,
  isLoading = false,
  hasLoaded = true,
  children,
  buttonId,
  buttonText,
  buttonDisabled,
  buttonOnClick,
  className,
  observeResize = true,
  onClose,
}: DialogFormProps) => {
  const { toasts } = useToast();
  const [escapeBlocked, setEscapeBlocked] = useState(false);

  useEffect(() => {
    const handleBlockEscape = (e: CustomEvent) => {
      setEscapeBlocked(e.detail.blocked);
    };

    window.addEventListener('dialog-escape-block' as any, handleBlockEscape);
    return () => {
      window.removeEventListener(
        'dialog-escape-block' as any,
        handleBlockEscape
      );
    };
  }, []);

  return (
    <Dialog
      open={showDialog}
      onOpenChange={(open) => {
        // Only allow closing if escape is not blocked by a child component
        if (!open && escapeBlocked) {
          return; // Prevent closing when changes are unsaved
        }
        onOpenChange(open);
        // Call onClose callback when dialog is actually closing
        if (!open && onClose) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="pt-12 pl-12 pr-8 !max-w-3xl lg:h-[760px] 2xl:h-[900px] h-[500px]"
        onClose={onClose}
      >
        <ToasterInDialog toasts={toasts}>
          <DialogHeader className="h-[32px]">
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          {isLoading && (
            <div className="lg:h-[500px] 2xl:h-[650px] h-[250px]">
              <LoadingSpinner />
            </div>
          )}
          {hasLoaded && (
            <ScrollArea className="pr-4" observeResize={observeResize}>
              <div
                className={cn(
                  'lg:h-[500px] 2xl:h-[650px] h-[250px] space-y-6 px-px',
                  className
                )}
              >
                {children}
              </div>
            </ScrollArea>
          )}
          <DialogFooter className="h-[64px] pr-4">
            <Button
              className="mt-4"
              id={buttonId}
              disabled={buttonDisabled}
              onClick={buttonOnClick}
              type="submit"
            >
              {buttonText}
            </Button>
          </DialogFooter>
        </ToasterInDialog>
      </DialogContent>
    </Dialog>
  );
};
